import React, { useState } from 'react';
import './CvManager.css';

const CvManager = ({ cvs = [], loading = false, loadCvs, isEditing = false }) => {
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCvName, setNewCvName] = useState('');
  const [newCvTitle, setNewCvTitle] = useState('');

  const handleCreateCv = async () => {
    if (!isEditing) {
      setMessage('Please enable edit mode to create CVs');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    if (!newCvName.trim() || !newCvTitle.trim()) {
      setMessage('Please enter both CV name and title');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    // Validate CV name (alphanumeric and hyphens only)
    const nameRegex = /^[a-zA-Z0-9-]+$/;
    if (!nameRegex.test(newCvName)) {
      setMessage('CV name can only contain letters, numbers, and hyphens');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8888/.netlify/functions/cv-create'
        : '/.netlify/functions/cv-create';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newCvName.trim(),
          title: newCvTitle.trim()
        })
      });

      if (response.ok) {
        setMessage(`CV "${newCvName}" created successfully!`);
        setMessageType('success');
        setNewCvName('');
        setNewCvTitle('');
        setShowCreateForm(false);
        loadCvs(); // Reload the list
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create CV');
      }
    } catch (error) {
      setMessage(`Failed to create CV: ${error.message}`);
      setMessageType('error');
    }

    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleDeleteCv = async (cvName) => {
    if (!isEditing) {
      setMessage('Please enable edit mode to delete CVs');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    if (cvName === 'default') {
      setMessage('Cannot delete the default CV');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    const confirmMessage = `Are you sure you want to delete the CV "${cvName}"? This action cannot be undone and will permanently remove all data for this CV.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8888/.netlify/functions/cv-delete'
        : '/.netlify/functions/cv-delete';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: cvName })
      });

      if (response.ok) {
        setMessage(`CV "${cvName}" deleted successfully!`);
        setMessageType('success');
        loadCvs(); // Reload the list
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete CV');
      }
    } catch (error) {
      setMessage(`Failed to delete CV: ${error.message}`);
      setMessageType('error');
    }

    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const getCvUrl = (cvName) => {
    const baseUrl = window.location.origin;
    if (cvName === 'default') {
      return baseUrl;
    }
    return `${baseUrl}/?name=${cvName}`;
  };

  if (loading) {
    return (
      <div className="cv-manager">
        <div className="cv-manager-header">
          <h3>CV Management</h3>
          <p>Loading CVs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-manager">
      <div className="cv-manager-header">
        <h3>CV Management</h3>
        <p>Manage multiple CVs. Each CV has its own URL and data.</p>
      </div>

      {message && (
        <div className={`cv-message ${messageType}`}>
          <i className={`fas ${messageType === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
          {message}
        </div>
      )}

      <div className="cv-list">
        {cvs.map((cv) => (
          <div key={cv.name} className="cv-item">
            <div className="cv-info">
              <div className="cv-name">
                <h4>{cv.name === 'default' ? 'Eli Elhadad (Default)' : cv.name}</h4>
                <span className="cv-title">{cv.title}</span>
              </div>
              <div className="cv-url">
                <label>URL:</label>
                <input 
                  type="text" 
                  value={getCvUrl(cv.name)} 
                  readOnly 
                  className="url-input"
                  onClick={(e) => e.target.select()}
                />
                <button 
                  className="copy-url-button"
                  onClick={() => {
                    navigator.clipboard.writeText(getCvUrl(cv.name));
                    setMessage('URL copied to clipboard!');
                    setMessageType('success');
                    setTimeout(() => {
                      setMessage('');
                      setMessageType('');
                    }, 2000);
                  }}
                  title="Copy URL to clipboard"
                >
                  <i className="fas fa-copy"></i>
                </button>
              </div>
              <div className="cv-meta">
                <span className="cv-created">Created: {new Date(cv.created_at).toLocaleDateString()}</span>
                {cv.updated_at && (
                  <span className="cv-updated">Updated: {new Date(cv.updated_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
            <div className="cv-actions">
              <a 
                href={getCvUrl(cv.name)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="view-cv-button"
              >
                <i className="fas fa-external-link-alt"></i>
                View CV
              </a>
              {isEditing && cv.name !== 'default' && (
                <button
                  onClick={() => handleDeleteCv(cv.name)}
                  className="delete-cv-button"
                  title="Delete this CV"
                >
                  <i className="fas fa-trash"></i>
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {isEditing && (
        <div className="cv-create-section">
          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              className="create-cv-button"
            >
              <i className="fas fa-plus"></i>
              Create New CV
            </button>
          ) : (
            <div className="create-cv-form">
              <h4>Create New CV</h4>
              <div className="form-group">
                <label>CV Name (URL identifier):</label>
                <input
                  type="text"
                  value={newCvName}
                  onChange={(e) => setNewCvName(e.target.value)}
                  placeholder="john-doe"
                  className="form-input"
                />
                <small>Only letters, numbers, and hyphens allowed</small>
              </div>
              <div className="form-group">
                <label>CV Title:</label>
                <input
                  type="text"
                  value={newCvTitle}
                  onChange={(e) => setNewCvTitle(e.target.value)}
                  placeholder="John Doe - Software Engineer"
                  className="form-input"
                />
              </div>
              <div className="form-actions">
                <button
                  onClick={handleCreateCv}
                  className="submit-button"
                >
                  <i className="fas fa-check"></i>
                  Create CV
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewCvName('');
                    setNewCvTitle('');
                  }}
                  className="cancel-button"
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="cv-info-section">
        <details>
          <summary>CV Management Help</summary>
          <div className="cv-help-content">
            <h4>How it works:</h4>
            <p>Each CV has a unique name that becomes part of its URL. The default CV (Eli Elhadad) is accessible at the base URL.</p>
            
            <h4>URLs:</h4>
            <p>• Default CV: <code>https://eli.elhadad.me/</code></p>
            <p>• Other CVs: <code>https://eli.elhadad.me/?name=john-doe</code></p>
            
            <h4>Creating CVs:</h4>
            <p>Use the "Create New CV" button to add new CVs. Each CV starts with empty data that you can fill in through the admin panel.</p>
            
            <h4>Managing Data:</h4>
            <p>Switch between CVs using the URL parameter, then use the regular admin panel to edit each CV's data.</p>
            
            <h4>Deleting CVs:</h4>
            <p>You can delete any CV except the default one. This action cannot be undone.</p>
          </div>
        </details>
      </div>
    </div>
  );
};

export default CvManager;
